/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console firebase */

import React from 'react';
import Screen from '../Screen';
import Combobox from '../../Combobox/Combobox';
import Label from '../../Label/Label';
import Button from '../../Button/Button';
import Service from '../../Util/Service';
import $ from '../../Util/Util';
import Datetime from '../../Util/Datetime';
import Firebase from '../../Util/Firebase';
import './Import.css';

/**
 * Represents the import screen.
 */

export default class Import extends Screen {

    /**
     * Default state.
     */

    state = {
        configurations:         [],
        config:                 null,
        started:                false,
        file:                   null,
        users:                  []
    }

    /** 
     * Event that fires when the user selects an import configuration.
     * 
     * @param   {Object}    config      selected configuration
     */

    _onConfigurationSelected = (config) => {
        this.setState({ config: config });
    }

    /** 
     * Event that fires when the user selects or deselects a file.
     */

    _onFileSelected = () => {
        this.setState({ file: (this.file.files && this.file.files.length > 0) ? this.file.files[0] : null });
    }

    /** 
     * Event that fires when the user start the import.
     */

    _start = () => {
        this.setState({ started: true }, () => {
            this.log.innerHTML = '';
            this._log(this.props.context.locale.import.log.message.started);

            // Sorting attributes by column

            this.state.config.attributes.sort((a, b) => {
                if (a.column < b.column) {
                    return -1;
                } else if (a.column > b.column) {
                    return 1;
                } else {
                    return 0;
                }                    
            });

            let readFile = () => {
                this._log(this.props.context.locale.import.log.message.process);

                let reader = new FileReader();

                reader.readAsText(this.state.file, 'UTF-8');
    
                reader.onload = (event) => {
                    let content = event.target.result.split(String.fromCharCode(10));

                    if (content.length > 0 && content[content.length - 1].trim() === '') {
                        content.splice(-1, 1);
                    }

                    this._log(`${this.props.context.locale.import.log.message.lines} ${content.length === 0 ? 0 : (content.length - (this.state.config.header ? 1 : 0))}`);

                    let primarykey = null;

                    for (let i = 0; i < this.state.config.attributes.length; i++) {
                        if (this.state.config.attributes[i].primarykey) {
                            primarykey = i;
                            break;
                        }
                    }

                    let primaryvalue = null;

                    for (let i = 0; i < this.state.config.entitytype.attributes.length; i++) {
                        if (this.state.config.entitytype.attributes[i].primary) {
                            primaryvalue = this.state.config.entitytype.attributes[i].id;
                            break;
                        }
                    }

                    if (primaryvalue) {
                        for (let i = 0; i < this.state.config.attributes.length; i++) {
                            if (this.state.config.attributes[i].id === primaryvalue) {
                                primaryvalue = i;
                                break;
                            }
                        }
                    }

                    let lineReader = (index, callback) => {
                        if (index >= content.length) {
                            callback();
                        } else {
                            if (!this.state.config.header || (index > 0 && this.state.config.header)) {
                                this._processLine(content[index], index + (this.state.config.header ? 0 : 1), primarykey, primaryvalue).then(() => {
                                    lineReader(index + 1, callback);
                                });
                            } else {
                                lineReader(index + 1, callback);
                            }
                        }
                    };
                    
                    lineReader(0, () => {
                        this._log(this.props.context.locale.import.log.message.finished);
                        this.setState({ started: false });    
                    });
                };
    
                reader.onerror = (event) => {
                    // No handler
                };
            };
            
            // Clearing previous data

            if (this.state.config.clear) {
                this._log(`${this.props.context.locale.import.log.message.delete} ${this.state.config.entitytype.name}`);

                Service.clearAllEntities(this.state.config.entitytype.id).then(() => {
                    this._log(this.props.context.locale.import.log.message.deleted);
                    readFile();
                }).catch((error) => {

                    // Hiding the progress indicator

                    this.props.onLoading(false);

                    // Showing error

                    $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
                });
            } else {
                readFile();
            }
        });
    }

    /**
     * Processes a line of the CSV file.
     * 
     * @param   {String}    line            line that has to process
     * @param   {Number}    row             row number
     * @param   {Number}    primarykey      index of the primarykey attribute
     * @param   {String}    primaryvalue    primary value of the entity
     * @return  {Promise}   promise         promise object
     */

    _processLine = (line, row, primarykey, primaryvalue) => {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            if (!line || line.length === 0) {
                resolve();
            } else {
                this._log(`${row}. ${this.props.context.locale.import.log.message.line}`);

                let data = line.split(',');
                let primarykeyvalue = null;

                if (primarykey !== null) {
                    if (data.length >= this.state.config.attributes[primarykey].column) {
                        let cell = data[this.state.config.attributes[primarykey].column - 1].trim();

                        if (!$.isEmpty(cell) && cell !== '') {
                            primarykeyvalue = this._getValue(cell, this.state.config.attributes[primarykey]);
                        }
                    }
                }

                if (primaryvalue !== null) {
                    if (data.length >= this.state.config.attributes[primaryvalue].column) {
                        let cell = data[this.state.config.attributes[primaryvalue].column - 1].trim();

                        if (!$.isEmpty(cell) && cell !== '') {
                            primaryvalue = this._getValue(cell, this.state.config.attributes[primaryvalue]);
                        }
                    }
                }

                this._getEntity(primarykey, primarykeyvalue, primaryvalue).then((result) => {
                    let entity = result.entity;
                    let version = result.version;
                    let uploadQueue = [];
                    let relationQueue = [];

                    this.state.config.attributes.forEach((attr) => {
                        if (data.length >= attr.column) {
                            let cell = data[attr.column - 1].trim();
    
                            if (!$.isEmpty(cell) && cell !== '') {
                                if (attr.relation) {
                                    relationQueue.push({ entitytype: attr.relation.entitytype, attribute: attr.relation.attribute, value: cell });
                                } else {
                                    if (attr.type === 'file' || attr.type === 'picture') {
                                        if (attr.multivalue) {
                                            entity.versions[version].attributes[attr.id] = [];
                                        }

                                        let cellparts = cell.split(';');
                                        let p = 0;

                                        while (true) {
                                            if (p >= cellparts.length) {
                                                break;
                                            }

                                            let name = cellparts[p];
                                            let mime = cellparts[p + 1];
                                            let content = cellparts[p + 2];
                                            let extension;
                                            let pos = name.lastIndexOf('.');
            
                                            if (pos >= 0) {
                                                extension = name.substring(pos + 1);
                                            } else {
                                                extension = '';
                                            }

                                            let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${version.substring(1)}-${attr.id}.${extension}`;
                                            uploadQueue.push({ 
                                                id:         attr.id, 
                                                index:      p / 3,
                                                name:       name,
                                                type:       attr.type,
                                                multivalue: attr.multivalue,
                                                content:    { url: url, value: content, mime: mime }
                                            });
                                            p+=3;
                                        }
                                    } else if (attr.type === 'user') {
                                        let email = this._getValue(cell, attr);

                                        for (let i = 0; i < this.state.users.length; i++) {
                                            if (this.state.users[i].email === email) {
                                                entity.versions[version].attributes[attr.id] = this.state.users[i].uid;
                                                break;
                                            }
                                        }                    
                                    } else {
                                        if (attr.multivalue) {
                                            entity.versions[version].attributes[attr.id] = [];
                                            let cellparts = cell.split(';');

                                            cellparts.forEach((c) => {
                                                entity.versions[version].attributes[attr.id].push(this._getValue(c, attr));
                                            });
                                        } else {
                                            entity.versions[version].attributes[attr.id] = this._getValue(cell, attr);
                                        }
                                    }
                                }
                            }
                        }                
                    });

                    // Saving

                    this._log(this.props.context.locale.import.log.message.save);

                    let uploader = (item) => {
                        return new Promise((resolve, reject) => {
                            Firebase.uploadFile(item.url, item.value, item.mime).then((url) => {
                                resolve(url);
                            }).catch((error) => {
                                this._log(this.props.context.locale.import.log.message.error);
                                reject();
                            });
                        });
                    };

                    let clearUploaderQueue = (index, callback) => {
                        if (index >= uploadQueue.length) {
                            callback();
                        } else {
                            uploader(uploadQueue[index].content).then((url) => {
                                if (uploadQueue[index].type === 'file') {
                                    if (uploadQueue[index].multivalue) {
                                        entity.versions[version].attributes[uploadQueue[index].id].push({ name: uploadQueue[index].name, url: url });
                                    } else {
                                        entity.versions[version].attributes[uploadQueue[index].id] = { name: uploadQueue[index].name, url: url };
                                    }
                                } else {
                                    if (uploadQueue[index].multivalue) {
                                        entity.versions[version].attributes[uploadQueue[index].id].push(url);
                                    } else {
                                        entity.versions[version].attributes[uploadQueue[index].id] = url;
                                    }
                                }
            
                                clearUploaderQueue(index + 1, callback);
                            });
                        }
                    };

                    let setRelation = (item) => {
                        return new Promise((resolve, reject) => {
                            Service.loadEntityByPrimaryKey(item.entitytype, item.attribute, item.value, true).then((result) => {
                                if (result) {
                                    let count = 0;

                                    result.forEach((r) => {
                                        Service.getNamedPath(r.entity.path).then((path) => {
                                            count++;
                                            
                                            if (!entity.relations) {
                                                entity.relations = [];
                                            }

                                            entity.relations.push(path);

                                            if (count >= result.length) {
                                                resolve();
                                            }
                                        });
                                    });
                                }
                            }).catch((error) => {
                                this._log(this.props.context.locale.import.log.message.error);
                                reject();
                            });
                        });
                    };

                    let clearRelationQueue = (index, callback) => {
                        if (index >= relationQueue.length) {
                            callback();
                        } else {
                            setRelation(relationQueue[index]).then(() => {
                                clearRelationQueue(index + 1, callback);
                            });
                        }
                    };

                    clearRelationQueue(0, () => {
                        clearUploaderQueue(0, () => {
                            Service.saveEntity(entity).then(() => {
                                resolve();
                            }).catch((error) => {
                                this._log(this.props.context.locale.import.log.message.error);
                                resolve();
                            });
                        });
                    });
                }).catch((error) => {
                    this._log(this.props.context.locale.import.log.message.error);
                    resolve();
                });
            }
        });        
    }

    _getEntity = (primarykey, primarykeyvalue, primaryvalue) => {
        return new Promise((resolve, reject) => {
            let newentity = {
                entitytype:     this.state.config.entitytype.id,
                path:           this.state.config.folder,
                primaryvalue:   primaryvalue,
                versions:       {
                    v1: {
                        attributes:         {},
                        modificationdate:   firebase.database.ServerValue.TIMESTAMP,
                        modifiedby:         this.props.context.user.uid
                    }
                }
            };

            if (this.state.config.append) {
                resolve({ entity: newentity, version: 'v1' });
            } else {
                if (primarykey !== null) {
                    Service.loadEntityByPrimaryKey(
                        this.state.config.entitytype.id,
                        this.state.config.attributes[primarykey].id,
                        primarykeyvalue
                    ).then((result) => {
                        if (result) {
                            let entity = result.entity;
                            let version = 'v' + (parseInt(result.version.substring(1), 10) + 1);
                            entity.versions[version] = {
                                attributes:         {},
                                modificationdate:   firebase.database.ServerValue.TIMESTAMP,
                                modifiedby:         this.props.context.user.uid
                            };

                            entity.primaryvalue = primaryvalue;
                            resolve({ entity: entity, version: version });
                        } else {
                            resolve({ entity: newentity, version: 'v1' });
                        }
                    }).catch((error) => {
                        reject(error);
                    });
                } else {
                    resolve({ entity: newentity, version: 'v1' });
                }
            }
        });
    }

    /**
     * Gets the value from the specified cell based on the specified attribute.
     * 
     * @param   {String}    cell        content of the cell
     * @param   {Object}    attribute   attribute for the cell
     * @return  {Any}       value       value of the cell
     */

    _getValue = (cell, attribute) => {
        if (attribute.type === 'text' || attribute.type === 'multilinetext' || attribute.type === 'externallink' || attribute.type === 'radio' || attribute.type === 'checkbox' || attribute.type === 'user') {
            return cell;
        } else if (attribute.type === 'integer') {
            return parseInt(cell, 10);
        } else if (attribute.type === 'float') {
            return parseFloat(cell, 10);
        } else if (attribute.type === 'date') {
            return Datetime.parseDate(cell, 'yyyy.mm.dd').getTime();
        } else if (attribute.type === 'picture' || attribute.type === 'file') {
            return cell;
        } else {
            return cell;
        }
    }

    /** 
     * Event that fires when the user stops the import.
     */

    _stop = () => {
        this._log(this.props.context.locale.import.log.message.finished);
        this.setState({ started: false });
    }

    /**
     * Logs the specified message.
     */

    _log = (message) => {
        this.log.innerHTML += `${new Datetime(new Date()).format('yyyy.mm.dd hh:MM:ss')} ${message}<br>`;
    }
    
    /** 
     * Event that fires when the screen mounted.
     */

    componentDidMount() {

        // Setting actionbar title

        this.props.onSetActionbarTitle(this.props.context.locale.import.title);

        // Showing progress indicator

        this.props.onLoading(true);

        // Loading the users
        
        Service.loadUser().then((users) => {
            this.setState({ users: users });

            // Loading the import configurations

            Service.loadImportConfiguration().then((configurations) => {

                // Storing the import configurations

                this.setState({ configurations: configurations }, () => {
                    if (configurations.length > 0) {
                        this.configname.setValue(configurations[0].id);
                        this._onConfigurationSelected(configurations[0]);
                    }

                    // Hiding progress indicator

                    this.props.onLoading(false);    
                });
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /** 
     * Import screen renderer.
     */

    render() {
        return (
            <div className="import">

                {/* Configuration chooser */}

                <Combobox ref={c => {this.configname = c;}} tooltip={this.props.context.locale.import.configurationname.tooltip} width={250} label={this.props.context.locale.import.configurationname.label} labelwidth={100} labelposition="left" disabled={this.state.started} idattr="id" nameattr="name" source={this.state.configurations} onSelect={this._onConfigurationSelected} />

                {/* File selector */}

                <div className="horizontal">
                    <Label text={this.props.context.locale.import.file.label} tooltip={this.props.context.locale.import.file.tooltip} width={100} />
                    <input ref={c => {this.file = c;}} disabled={this.state.started} type="file" className="import-file" accept=".csv" onChange={this._onFileSelected} />
                </div>

                {/* Buttons */}

                <div className="horizontal">
                    <Button label={this.props.context.locale.import.start.label} tooltip={this.props.context.locale.import.start.tooltip} width={80} leftgap={100} topgap={16} disabled={this.state.started || !this.state.file || this.state.configurations.length === 0} onTouch={this._start} />
                    <Button label={this.props.context.locale.import.stop.label} tooltip={this.props.context.locale.import.stop.tooltip} width={80} leftgap={16} topgap={16} disabled={!this.state.started || !this.state.file} onTouch={this._stop} />
                </div>

                {/* Log */}

                <Label text={this.props.context.locale.import.log.label} tooltip={this.props.context.locale.import.log.tooltip} width={100} topgap={16} />
                <div ref={c => {this.log = c;}} className="import-log" style={{ height: (this.props.context.environment.screen.usefullheight - 210) + 'px'}}></div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Screens/Import/Import.js